import { Button } from "@/modules/shadcn/ui/button";
import { cn } from "@/lib/utils";
import type { EvalAction } from "@repo/schema";
import { ChevronDownIcon, Trash2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { descriptorFor } from "./actionRegistry";
import type { ActionFieldsProps } from "./actionTypes";

type ActionRowProps = Pick<ActionFieldsProps, "index" | "actions"> & {
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
};

export function ActionRow({
  index,
  actions,
  expanded,
  onToggle,
  onRemove,
}: ActionRowProps) {
  const { t } = useTranslation(["evaluation", "common"]);
  const action: EvalAction | undefined = actions[index];
  if (!action) return null;

  const descriptor = descriptorFor(action.type);
  // An unknown type means the row was written by a newer Studio than this build.
  // Render it as read-only rather than dropping it, so saving cannot silently
  // discard configuration this UI does not understand yet.
  if (!descriptor) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="text-sm text-muted-foreground">
          {t("actions.unsupported", { type: action.type })}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={onRemove}
          aria-label={t("actions.remove", { label: action.type })}
        >
          <Trash2Icon className="size-4 text-destructive" />
        </Button>
      </div>
    );
  }

  const { Icon, summarize, Fields } = descriptor;
  const label = t(descriptor.label);
  const summary = summarize?.(action, t);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <Icon className="size-4 shrink-0 text-muted-foreground" />
        <span className="text-sm font-medium">{label}</span>

        {summary && (
          <span className="truncate text-xs text-muted-foreground">
            {summary}
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          {Fields && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={t(expanded ? "actions.hideSettings" : "actions.showSettings", {
                label,
              })}
            >
              <ChevronDownIcon
                className={cn(
                  "size-4 transition-transform",
                  expanded && "rotate-180",
                )}
              />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            aria-label={t("actions.remove", { label })}
          >
            <Trash2Icon className="size-4 text-destructive" />
          </Button>
        </div>
      </div>

      {Fields && expanded && (
        <div className="border-t border-border bg-muted/20 px-3 py-3">
          <Fields index={index} actions={actions} />
        </div>
      )}
    </div>
  );
}
